import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Lock, Mail, Loader2, CheckCircle2, XCircle, Phone, Plus,
  TrendingUp, Euro, Users, Target, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// keep axios cookies isolated to this portal flow (don't reuse logged-in admin session)
const portalAxios = axios.create({ baseURL: API, withCredentials: true });

// Build a compact 2-line content summary from the body. Skips boilerplate
// like form titles, repeated labels alone on lines, etc. — keeps actual
// answers ("Je souhaite: prendre un rendez-vous", "Boubakary", "0612345678"…).
function summarizeBody(body) {
  if (!body) return '';
  // Strip URLs, HTML, "soumis le DD/MM/YYYY" date noise
  let clean = body
    .replace(/<[^>]+>/g, ' ')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\bsoumis\s+le\s+\d{1,2}\/\d{1,2}\/\d{2,4}/gi, '');
  // Squeeze whitespace
  clean = clean.replace(/\s+/g, ' ').trim();
  return clean.slice(0, 220) + (clean.length > 220 ? '…' : '');
}

// Build a short key/value summary list (e.g. "Boubakary · 06 12 34 56 78 · boubakary@example.fr")
function summarizeExtractedFields(ef) {
  if (!ef) return [];
  const out = [];
  const fullName = ef.full_name || [ef.first_name, ef.last_name].filter(Boolean).join(' ').trim();
  if (fullName) out.push(fullName);
  if (ef.phone) out.push(ef.phone);
  if (ef.email) out.push(ef.email);
  if (ef.subject_request) out.push(`« ${ef.subject_request} »`);
  return out;
}

const STATUS_LABELS = {
  new: { label: 'Nouveau', color: 'bg-neutral-100 text-neutral-700 border-neutral-200' },
  qualified: { label: 'Qualifié', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  unqualified: { label: 'Non qualifié', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  won: { label: 'Gagné', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  lost: { label: 'Perdu', color: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const LOSS_REASON_LABELS = {
  prix: 'Prix trop élevé',
  delai: 'Délai trop long',
  concurrent: 'Choisi un concurrent',
  ne_repond_plus: 'Ne répond plus',
  hors_zone: 'Hors zone d\'intervention',
  pas_serieux: 'Pas sérieux / spam',
  autre: 'Autre',
};

const formatEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const formatDateTime = (s) => s ? new Date(s).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const formatDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';

export default function ClientPortal() {
  const { token } = useParams();
  const [phase, setPhase] = useState('init'); // init | landing | pin | dashboard | error
  const [info, setInfo] = useState(null);
  const [pinSent, setPinSent] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [error, setError] = useState('');

  const loadInfo = useCallback(async () => {
    try {
      const { data } = await portalAxios.get(`/client-portal/public/${token}/info`);
      setInfo(data);
      setPhase('landing');
    } catch (e) {
      setError(e.response?.data?.detail || 'Lien invalide ou expiré.');
      setPhase('error');
    }
  }, [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await portalAxios.get(`/client-portal/public/${token}/data?months=6`);
      setData(data);
      setPhase('dashboard');
      return true;
    } catch (e) {
      if (e.response?.status === 401) return false; // not authenticated yet — caller decides
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // On first mount, try to load data with existing cookie. If 401 → show landing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await loadData();
      if (!ok && !cancelled) {
        await loadInfo();
      }
    })();
    return () => { cancelled = true; };
  }, [loadData, loadInfo]);

  const requestPin = async () => {
    setLoading(true);
    try {
      const { data } = await portalAxios.post(`/client-portal/public/${token}/request-pin`);
      setPinSent(true);
      setPhase('pin');
      toast.success(`Code envoyé à ${data.sent_to_count} destinataire(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi code');
    } finally {
      setLoading(false);
    }
  };

  const verifyPin = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(pin)) {
      toast.error('Code à 6 chiffres requis');
      return;
    }
    setLoading(true);
    try {
      await portalAxios.post(`/client-portal/public/${token}/verify-pin`, { pin });
      setPin('');
      const ok = await loadData();
      if (!ok) {
        // Cookie was set but the follow-up /data call returned 401.
        // Almost always means cross-site cookie was dropped by the browser
        // (3rd-party-cookie blocking, ITP, or older deploy with samesite=lax).
        toast.error(
          "Code accepté, mais la session n'a pas pu être ouverte. " +
          "Veuillez désactiver le blocage des cookies tiers pour ce site, ou réessayer."
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  const updateLead = async (leadId, payload) => {
    try {
      const { data: updated } = await portalAxios.put(`/client-portal/public/${token}/lead/${leadId}`, payload);
      setData((d) => ({ ...d, leads: d.leads.map(l => l.id === leadId ? updated : l) }));
      toast.success('Lead mis à jour');
      setEditLead(null);
      // Re-fetch KPIs in background to keep numbers fresh
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    }
  };

  const dismissDuplicate = async (leadId) => {
    try {
      await portalAxios.post(`/client-portal/public/${token}/lead/${leadId}/dismiss-duplicate`);
      setData((d) => ({ ...d, leads: d.leads.map(l => l.id === leadId ? { ...l, is_duplicate: false, duplicate_of: null } : l) }));
      toast.success('Marqué comme non-doublon');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const addPhoneCall = async (payload) => {
    try {
      const { data: created } = await portalAxios.post(`/client-portal/public/${token}/phone-call`, payload);
      setData((d) => ({ ...d, leads: [created, ...d.leads] }));
      toast.success('Appel ajouté à la liste');
      setPhoneOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur ajout appel');
    }
  };

  // ─── ERROR ──────────
  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-blue-50 p-4">
        <div className="max-w-md text-center space-y-3">
          <XCircle className="w-12 h-12 mx-auto text-rose-500" />
          <h1 className="text-2xl font-semibold text-neutral-900">Lien invalide</h1>
          <p className="text-sm text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  // ─── INIT ──────────
  if (phase === 'init') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  // ─── LANDING (request PIN) ──────────
  if (phase === 'landing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-blue-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 space-y-5" data-testid="portal-landing">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#002FA7] text-white">
              <Lock className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-semibold text-neutral-900">Espace leads — {info?.client_name}</h1>
            <p className="text-sm text-neutral-600">
              Pour accéder à vos leads, nous allons vous envoyer un code à 6 chiffres par email.
            </p>
          </div>

          {info?.masked_recipients?.length > 0 ? (
            <div className="bg-neutral-50 border border-neutral-200 rounded p-3 text-xs text-neutral-600">
              Le code sera envoyé à&nbsp;:
              <ul className="mt-1 space-y-0.5">
                {info.masked_recipients.map((m, i) => (
                  <li key={i} className="font-mono flex items-center gap-1.5"><Mail className="w-3 h-3" />{m}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-700">
              Aucun destinataire enregistré. Contactez votre référent Aegis.
            </div>
          )}

          <Button
            onClick={requestPin}
            disabled={loading || !info?.recipient_count}
            className="w-full bg-[#002FA7] hover:bg-[#002277]"
            data-testid="request-pin-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Recevoir mon code
          </Button>
        </div>
      </div>
    );
  }

  // ─── PIN ENTRY ──────────
  if (phase === 'pin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-blue-50 p-4">
        <form onSubmit={verifyPin} className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 space-y-5" data-testid="portal-pin">
          <div className="text-center space-y-2">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
            <h1 className="text-xl font-semibold text-neutral-900">Code envoyé</h1>
            <p className="text-sm text-neutral-600">Saisissez le code à 6 chiffres reçu par email (valide 15 min).</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Code reçu</Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="text-center text-2xl tracking-widest font-mono h-14"
              data-testid="pin-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading || pin.length !== 6}
            className="w-full bg-[#002FA7] hover:bg-[#002277]"
            data-testid="verify-pin-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Valider
          </Button>

          <button
            type="button"
            onClick={requestPin}
            className="text-xs text-neutral-500 hover:text-[#002FA7] underline w-full text-center"
            data-testid="resend-pin"
          >
            Renvoyer un nouveau code
          </button>
        </form>
      </div>
    );
  }

  // ─── DASHBOARD ──────────
  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-lg font-semibold text-neutral-900">{data?.client_name}</h1>
            <p className="text-xs text-neutral-500">Vos leads & ventes — espace sécurisé</p>
          </div>
          <Button onClick={loadData} variant="outline" size="sm" disabled={loading} data-testid="portal-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-kpis">
          <KpiCard icon={<Users className="w-4 h-4" />} label="Leads ce mois" value={data?.kpi?.month_total_leads ?? 0} />
          <KpiCard icon={<Target className="w-4 h-4" />} label="Ventes signées" value={data?.kpi?.month_won ?? 0} />
          <KpiCard icon={<TrendingUp className="w-4 h-4" />} label="Taux de conv." value={`${((data?.kpi?.month_conversion_rate ?? 0) * 100).toFixed(0)}%`} />
          <KpiCard icon={<Euro className="w-4 h-4" />} label="CA du mois" value={formatEUR(data?.kpi?.month_revenue ?? 0)} highlight />
        </div>

        {/* Add phone call CTA */}
        <div className="flex items-center justify-between bg-white border border-neutral-200 rounded p-4">
          <div>
            <h2 className="font-medium text-neutral-900">Appel téléphonique reçu ?</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Ajoutez-le à la liste pour qu'il compte dans vos KPIs.</p>
          </div>
          <Button onClick={() => setPhoneOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-phone-btn">
            <Phone className="w-4 h-4 mr-1" /> Ajouter un appel
          </Button>
        </div>

        {/* Leads list */}
        <div className="bg-white border border-neutral-200 rounded overflow-hidden">
          <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="font-medium text-neutral-900">Mes leads ({data?.leads?.length ?? 0})</h2>
            <span className="text-xs text-neutral-500">Changez le statut directement ci-dessous — cliquez sur la ligne pour ajouter notes / CA</span>
          </div>
          {!data?.leads?.length ? (
            <div className="p-12 text-center text-neutral-500">
              Aucun lead à qualifier pour le moment.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Sujet / Contenu</th>
                  <th className="px-4 py-2 text-left">Statut</th>
                  <th className="px-4 py-2 text-right">CA</th>
                </tr>
              </thead>
              <tbody>
                {data.leads.map((lead) => {
                  const extractedSummary = summarizeExtractedFields(lead.extracted_fields);
                  const bodySnippet = summarizeBody(lead.body_preview);
                  return (
                  <tr
                    key={lead.id}
                    className="border-t border-neutral-100 hover:bg-blue-50/40"
                    data-testid={`portal-lead-${lead.id}`}
                  >
                    <td className="px-4 py-3 text-xs text-neutral-500 whitespace-nowrap align-top">{formatDate(lead.received_at)}</td>
                    <td className="px-4 py-3 align-top">
                      {lead.kind === 'phone_call' ? (
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[10px]"><Phone className="w-3 h-3 mr-1" />Appel</Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]"><Mail className="w-3 h-3 mr-1" />Email</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top max-w-xl cursor-pointer" onClick={() => setEditLead(lead)} title="Cliquez pour ajouter notes ou CA">
                      <div className="text-neutral-900 font-medium flex items-center gap-1.5 mb-0.5">
                        <span className="truncate">{lead.subject || '(sans sujet)'}</span>
                        {lead.is_duplicate && (
                          <span className="text-[9px] px-1 py-0 rounded border border-amber-200 bg-amber-100 text-amber-800 shrink-0" title="Doublon présumé d'un lead antérieur">
                            Doublon ?
                          </span>
                        )}
                      </div>
                      {extractedSummary.length > 0 ? (
                        <div className="text-xs text-neutral-700 flex flex-wrap gap-x-2 gap-y-0.5">
                          {extractedSummary.map((v, i) => (
                            <span key={i} className={i === 0 ? 'font-medium text-neutral-900' : ''}>{i > 0 && <span className="text-neutral-300 mr-1">·</span>}{v}</span>
                          ))}
                        </div>
                      ) : null}
                      {bodySnippet && (
                        <div className="text-xs text-neutral-500 mt-1 line-clamp-2 leading-snug">{bodySnippet}</div>
                      )}
                      {lead.unqualified_reason && lead.status === 'unqualified' && (
                        <div className="text-[11px] text-amber-700 mt-1 italic">↳ {lead.unqualified_reason}</div>
                      )}
                      {lead.loss_reason && lead.status === 'lost' && (
                        <div className="text-[11px] text-rose-700 mt-1 italic">↳ {LOSS_REASON_LABELS[lead.loss_reason] || lead.loss_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                      <InlineStatusPicker
                        lead={lead}
                        onChange={(payload) => updateLead(lead.id, payload)}
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs align-top">
                      {lead.status === 'won' ? formatEUR(lead.sale_value) : '—'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Lead edit dialog */}
      <LeadQualifyDialog
        lead={editLead}
        open={!!editLead}
        loss_reasons={data?.loss_reasons || []}
        onOpenChange={(o) => { if (!o) setEditLead(null); }}
        onSave={(payload) => editLead && updateLead(editLead.id, payload)}
        onDismissDuplicate={() => editLead && dismissDuplicate(editLead.id)}
      />

      {/* Phone call dialog */}
      <PhoneCallDialog
        open={phoneOpen}
        onOpenChange={setPhoneOpen}
        onSave={addPhoneCall}
      />
    </div>
  );
}

function KpiCard({ icon, label, value, highlight }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-neutral-200'}`}>
      <div className={`flex items-center gap-1.5 text-xs ${highlight ? 'text-blue-100' : 'text-neutral-500'}`}>
        {icon}
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${highlight ? 'text-white' : 'text-neutral-900'}`}>{value}</div>
    </div>
  );
}

// Inline status dropdown shown on each lead row. Bypasses the modal for
// the most common action (just changing status). Prompts for reason/value
// when status requires it (won → CA, unqualified → reason).
function InlineStatusPicker({ lead, onChange }) {
  const handleChange = (newStatus) => {
    if (newStatus === lead.status) return;
    if (newStatus === 'won') {
      const raw = window.prompt("Montant de la vente en € (chiffre d'affaires) :", lead.sale_value || '');
      if (raw === null) return;
      const sale_value = parseFloat(raw.replace(',', '.')) || 0;
      onChange({ status: 'won', sale_value });
      return;
    }
    if (newStatus === 'unqualified') {
      const reason = window.prompt(
        "Raison pour laquelle ce lead n'est pas qualifié ?\n(courte description — sera réutilisée comme modèle)",
        lead.unqualified_reason || '',
      );
      if (reason === null) return;
      onChange({ status: 'unqualified', unqualified_reason: reason.trim() });
      return;
    }
    if (newStatus === 'lost') {
      // Don't force loss_reason here — user can fine-tune via modal if needed
      onChange({ status: 'lost' });
      return;
    }
    onChange({ status: newStatus });
  };
  const meta = STATUS_LABELS[lead.status] || STATUS_LABELS.new;
  return (
    <Select value={lead.status || 'new'} onValueChange={handleChange}>
      <SelectTrigger
        className={`h-8 w-[140px] text-xs border ${meta.color}`}
        data-testid={`status-picker-${lead.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="new">Nouveau</SelectItem>
        <SelectItem value="qualified">Qualifié</SelectItem>
        <SelectItem value="unqualified">Non qualifié</SelectItem>
        <SelectItem value="won">Gagné</SelectItem>
        <SelectItem value="lost">Perdu</SelectItem>
      </SelectContent>
    </Select>
  );
}

function LeadQualifyDialog({ lead, open, onOpenChange, onSave, loss_reasons, onDismissDuplicate }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (lead) setForm({
      status: lead.status || 'new',
      sale_value: lead.sale_value || 0,
      notes: lead.notes || '',
      loss_reason: lead.loss_reason || '',
      unqualified_reason: lead.unqualified_reason || '',
    });
  }, [lead]);

  const utms = useMemo(() => {
    if (!lead) return [];
    const text = `${lead.subject || ''}\n${lead.body_preview || ''}`;
    const out = [];
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'gclid', 'fbclid'].forEach(k => {
      const m = new RegExp(`${k}\\s*[=:]\\s*([\\w\\-.+%]+)`, 'i').exec(text);
      if (m) out.push([k, decodeURIComponent(m[1])]);
    });
    return out;
  }, [lead]);

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="portal-lead-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lead.subject || '(sans sujet)'}</DialogTitle>
          <DialogDescription className="text-xs flex flex-wrap items-center gap-2 mt-1">
            <span>Reçu&nbsp;: <strong>{formatDateTime(lead.received_at)}</strong></span>
            <span className="text-neutral-300">·</span>
            <span className="font-mono text-[11px] truncate max-w-[260px]">{lead.from_email}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {lead.is_duplicate && (
            <div className="bg-amber-50 border border-amber-300 rounded p-3 flex items-center justify-between gap-3" data-testid="portal-dup-banner">
              <div className="text-xs text-amber-900">
                <strong>Doublon présumé</strong>
                <p className="mt-0.5">Ce lead ressemble fortement à un autre reçu dans les 48h. Vérifiez avant qualification.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0"
                onClick={onDismissDuplicate}
              >
                Pas un doublon
              </Button>
            </div>
          )}
          {lead.extracted_fields && Object.keys(lead.extracted_fields).length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3" data-testid="portal-lead-fields">
              <p className="text-xs font-medium text-blue-900 mb-2">Informations du formulaire</p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                {[
                  ['full_name', 'Nom complet'],
                  ['email', 'Email'],
                  ['phone', 'Téléphone'],
                  ['company', 'Société'],
                  ['address', 'Adresse'],
                  ['postal_code', 'CP'],
                  ['city', 'Ville'],
                  ['surface', 'Surface'],
                  ['subject_request', 'Projet'],
                  ['message', 'Message'],
                ].map(([key, label]) => {
                  const v = lead.extracted_fields[key];
                  if (!v) return null;
                  const isFull = ['address', 'subject_request', 'message'].includes(key);
                  return (
                    <div key={key} className={isFull ? 'sm:col-span-2' : ''}>
                      <dt className="text-blue-700 font-medium uppercase tracking-wide text-[10px]">{label}</dt>
                      <dd className="text-neutral-900 mt-0.5 break-words">{v}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}

          {utms.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {utms.map(([k, v]) => (
                <Badge key={k} className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] border">
                  {k} : {v}
                </Badge>
              ))}
            </div>
          )}

          {lead.body_preview && (
            <div className="space-y-1.5">
              <Label className="text-xs">Contenu du formulaire</Label>
              <div className="bg-neutral-50 border border-neutral-200 rounded p-3 text-xs text-neutral-700 whitespace-pre-wrap font-mono leading-relaxed max-h-60 overflow-y-auto">
                {lead.body_preview}
              </div>
            </div>
          )}

          <hr className="border-neutral-200" />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Nouveau</SelectItem>
                  <SelectItem value="qualified">Qualifié</SelectItem>
                  <SelectItem value="unqualified">Non qualifié</SelectItem>
                  <SelectItem value="won">Gagné</SelectItem>
                  <SelectItem value="lost">Perdu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.status === 'won' && (
              <div className="space-y-1.5">
                <Label className="text-xs">CA réel (€)</Label>
                <Input
                  type="number"
                  min={0}
                  step={50}
                  value={form.sale_value}
                  onChange={(e) => setForm({ ...form, sale_value: parseFloat(e.target.value) || 0 })}
                  data-testid="sale-value-input"
                />
              </div>
            )}
            {form.status === 'lost' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Raison de la perte</Label>
                <Select value={form.loss_reason} onValueChange={(v) => setForm({ ...form, loss_reason: v })}>
                  <SelectTrigger data-testid="loss-reason-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {loss_reasons.map(r => (
                      <SelectItem key={r} value={r}>{LOSS_REASON_LABELS[r] || r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.status === 'unqualified' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Raison du non-qualifié</Label>
                <Input
                  value={form.unqualified_reason}
                  onChange={(e) => setForm({ ...form, unqualified_reason: e.target.value })}
                  placeholder="ex. Hors zone, mauvais numéro, demande non sérieuse…"
                  data-testid="unqualified-reason-input"
                />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes (optionnel)</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Détails de la vente, commentaire..." />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={() => onSave(form)} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="save-lead-btn">
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PhoneCallDialog({ open, onOpenChange, onSave }) {
  const [form, setForm] = useState({
    caller_name: '',
    caller_phone: '',
    subject: '',
    notes: '',
    suspected_source: '',
    received_at: new Date().toISOString().slice(0, 16),
  });
  useEffect(() => {
    if (open) {
      setForm({
        caller_name: '',
        caller_phone: '',
        subject: '',
        notes: '',
        suspected_source: '',
        received_at: new Date().toISOString().slice(0, 16),
      });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="phone-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5 text-emerald-600" /> Ajouter un appel reçu</DialogTitle>
          <DialogDescription className="text-xs">Saisissez les informations connues. Au moins un nom OU un téléphone est requis.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Nom de l'appelant</Label>
              <Input value={form.caller_name} onChange={(e) => setForm({ ...form, caller_name: e.target.value })} placeholder="Jean Dupont" data-testid="caller-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Téléphone</Label>
              <Input value={form.caller_phone} onChange={(e) => setForm({ ...form, caller_phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="caller-phone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Sujet de l'appel</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Demande de devis pour..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Source supposée</Label>
              <Select value={form.suspected_source} onValueChange={(v) => setForm({ ...form, suspected_source: v })}>
                <SelectTrigger data-testid="suspected-source"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="google">Google (organique)</SelectItem>
                  <SelectItem value="google_ads">Google Ads (annonce)</SelectItem>
                  <SelectItem value="recommandation">Recommandation</SelectItem>
                  <SelectItem value="site_web">Site web</SelectItem>
                  <SelectItem value="annuaire">Annuaire / Pages Jaunes</SelectItem>
                  <SelectItem value="autre">Autre / Inconnue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date / heure de l'appel</Label>
              <Input type="datetime-local" value={form.received_at} onChange={(e) => setForm({ ...form, received_at: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Détails de la conversation, projet, urgence..." />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button
              onClick={() => onSave(form)}
              disabled={!form.caller_name && !form.caller_phone}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-phone-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
